// import Image from "next/image"
import { DateTime } from "luxon"
import { useTranslation } from "next-i18next"
import { Image, StructuredText } from "react-datocms"
import React from "react"
import { GetServerSideProps, NextPage } from "next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import styled from "styled-components"

import { initDatoClient } from "@aistihealth/web-common/src/datocms/gql/client/apollo-client"
import {
  GetArticleBySlugDocument,
  GetArticleBySlugQuery,
  GetArticleBySlugQueryVariables,
  ImgixParamsAuto,
} from "@aistihealth/web-common/src/datocms/gql/generated/graphql"
import { localeToDatoLocale } from "@aistihealth/web-common/src/datocms/dato-utils"
import { theme } from "@aistihealth/web-common/src/util/styles/theme"
import { Heading } from "@aistihealth/web-common/src/components/heading/Heading"
import { Box } from "@aistihealth/web-common/src/components/box/Box"
import { Button } from "@aistihealth/web-common/src/components/button/Button"
import {
  blockquoteRenderRule,
  headingRenderRule,
  linkRenderRule,
  paragraphRenderRule,
} from "@aistihealth/web-common/src/datocms/renderNodeRules"
import { Quote } from "@aistihealth/web-common/src/components/quote/Quote"
import { SingleColLayout } from "@aistihealth/web-common/src/components/single-col-layout/SingleColLayout"
import { NextStepsSection } from "../../components/next-steps-section/NextStepsSection"
import { Section } from "../../components/section/Section"
import { MainLayout } from "../../components/layouts/main-layout/MainLayout"
import { BasePageProps } from "../../util/page"

const token = process.env.DATOCMS_API_TOKEN
const environment = process.env.DATOCMS_ENVIRONMENT

export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
  preview,
}) => {
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug
  const datoClient = initDatoClient({ token, environment, preview })
  const response = await datoClient.query<
    GetArticleBySlugQuery,
    GetArticleBySlugQueryVariables
  >({
    query: GetArticleBySlugDocument,
    variables: {
      imgixParams: {
        auto: [ImgixParamsAuto.format],
        h: 550,
      },
      locale: localeToDatoLocale(locale),
      slug,
    },
  })

  if (!response.data.article) {
    return { notFound: true }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      article: response.data.article,
      footer: response.data.marketingSitePageStructure.footer[0],
      titleSuffix: response.data.site.globalSeo.titleSuffix,
    },
  }
}

type ArticleProps = {
  article: GetArticleBySlugQuery["article"]
  titleSuffix: string
} & BasePageProps

const ArticleDate = styled.time`
  ${theme.textStyle("default", "medium", "normal")};
  color: ${theme.colors.greyDark};
`

const ImageContainer = styled(Box)`
  padding-top: 0;
  padding-left: 0;
  padding-right: 0;
`

const ButtonContainer = styled(Box)`
  margin-bottom: ${theme.spacings.xlarge};
`
const BackButton = styled(Button).attrs({
  icon: "arrowBack",
  size: "small",
  type: "link",
  variant: "secondary",
})`
  margin-bottom: ${theme.spacings.xlarge};

  svg {
    font-size: 200%;
  }
`

const Link = styled.a`
  text-decoration: none;
  color: ${theme.colors.orange};
`

const QuoteComponent = styled(Quote)`
  text-decoration: none;
  color: ${theme.colors.orange};
`

const articleRenderRules = [
  paragraphRenderRule({
    size: "medium",
  }),
  headingRenderRule({
    1: { size: "xlarge", weight: "bold" },
    2: { size: "xlarge", weight: "medium" },
    3: { size: "large", weight: "medium" },
    4: { size: "medium", weight: "medium" },
    5: { size: "small", weight: "medium" },
    6: { size: "xsmall", weight: "medium" },
  }),
  linkRenderRule(Link),
  blockquoteRenderRule(QuoteComponent),
]

const Article: NextPage<ArticleProps> = ({ article, titleSuffix, footer }) => {
  const { t } = useTranslation()

  return (
    <MainLayout
      type="metaTags"
      topBackground="roseGoldLight"
      bottomBackground="yellow"
      metaTags={article.seoMetaTags}
      titleSuffix={titleSuffix}
      footer={footer}
    >
      <Section backgroundColor="roseGoldLight">
        <SingleColLayout colProps={{ md: 10, mdOffset: 1, sm: 12 }}>
          <ButtonContainer backgroundColor="transparent" padding={false}>
            <BackButton href="/blog">{t("components.buttons.back")}</BackButton>
          </ButtonContainer>
          <Box shadow="flat">
            <ImageContainer>
              <Image
                data={article.image.responsiveImage}
                style={{ borderRadius: theme.borderRadius.medium }}
              />
            </ImageContainer>
            <ArticleDate dateTime={article.date}>
              {DateTime.fromISO(article.date).toFormat(
                t("common:formats.date.short"),
              )}
            </ArticleDate>
            <Heading size="xlarge" weight="bold" as="h1">
              {article.title}
            </Heading>
            <StructuredText
              data={article.content.value}
              customNodeRules={articleRenderRules}
            />
          </Box>
        </SingleColLayout>
      </Section>
      <Section backgroundColor="yellow">
        <NextStepsSection />
      </Section>
    </MainLayout>
  )
}

export default Article
